import React, { useState, useEffect, useRef, FormEvent, ChangeEvent, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import axios, { AxiosError } from 'axios';
import { GeminiAPIResponse } from '../../types';
import { Mic, Paperclip, ArrowRight, Loader2 } from 'lucide-react';

const Searchbar: React.FC = () => {
    const [inputValue, setInputValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const navigate = useNavigate();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { theme } = useTheme();

    const defaultPrompts = [
        { label: "Damages in Commercial Cases", icon: "⚖️", prompt: "What are the guidelines for awarding damages in high-value commercial cases?" },
        { label: "Commercial Court Act", icon: "📜", prompt: "Explain Section 4 of Indian Commercial Court Act 2015" },
        { label: "Property Judgments", icon: "🏘️", prompt: "Summarize recent Indian Supreme Court judgments on property rights" },
        { label: "Procedural Timelines", icon: "⏳", prompt: "What are the procedural timelines for commercial cases under the Commercial Courts Act?" }
    ];

    useEffect(() => {
        adjustTextareaHeight();
    }, [inputValue]);

    const adjustTextareaHeight = (): void => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
        setInputValue(e.target.value);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSearchSubmit(e as unknown as FormEvent);
        }
    };

    const callGeminiApi = async (query: string): Promise<GeminiAPIResponse> => {
        setIsLoading(true);
        const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
        const API_ENDPOINT = `https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash:generateContent?key=${API_KEY}`;

        try {
            const response = await axios.post<GeminiAPIResponse>(API_ENDPOINT, {
                contents: [{
                    parts: [{
                        text: `You are a legal research engine for Indian commercial courts. Provide CONCISE responses with key points only. 

Query: ${query}

Instructions:
- Keep response brief and focused (max 300 words)
- Use bullet points for clarity
- Mention 2-3 relevant historical cases if applicable
- End with: "Note: This AI research is suggestive only and should be verified for legal judgments."`
                    }]
                }]
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            console.log('API Response:', response.data);
            setIsLoading(false);
            return response.data;
        } catch (error) {
            console.error('Error calling Gemini API:', error);
            setIsLoading(false);
            let errorMessage = 'An unknown error occurred.';

            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<any>;
                if (axiosError.response) {
                    errorMessage = axiosError.response.data?.error?.message ||
                        axiosError.response.statusText ||
                        `Server error: ${axiosError.response.status}`;
                } else if (axiosError.request) {
                    errorMessage = 'No response received from the server. Please check your network connection.';
                } else {
                    errorMessage = axiosError.message;
                }
            }
            return { error: `Failed to get a response: ${errorMessage}` };
        }
    };

    const handleSearchSubmit = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        if (inputValue.trim()) {
            const query = inputValue.trim();

            // Save to History
            try {
                const historyItem = {
                    id: Date.now().toString(),
                    title: query,
                    timestamp: Date.now()
                };

                const existingHistory = localStorage.getItem('verdictai_chat_history');
                const history = existingHistory ? JSON.parse(existingHistory) : [];

                // Add new item to the beginning, limit to last 50 items
                const updatedHistory = [historyItem, ...history].slice(0, 50);
                localStorage.setItem('verdictai_chat_history', JSON.stringify(updatedHistory));

                // Dispatch a custom event so HistoryBar can update if it's mounted
                window.dispatchEvent(new Event('storage'));
            } catch (error) {
                console.error('Failed to save history:', error);
            }

            const apiResponse = await callGeminiApi(query);
            navigate('/search-results', {
                state: {
                    searchQuery: query,
                    apiResponse: apiResponse
                }
            });
        }
    };

    return (
        <div className="w-full flex flex-col items-center space-y-8 z-20 relative px-4">
            <div className={`
                relative w-full max-w-2xl rounded-2xl transition-all duration-300
                ${isFocused ? 'shadow-2xl scale-[1.01]' : 'shadow-lg hover:shadow-xl'}
                ${theme === 'dark' ? 'bg-dark-bg-elevated shadow-black/20' : 'bg-white shadow-gray-200/50'}
            `}>
                {/* Gradient Border Effect when Focused */}
                <div className={`
                    absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-primary-400 via-primary-600 to-primary-400 opacity-0 transition-opacity duration-500
                    ${isFocused ? 'opacity-100' : ''}
                `} />

                <form onSubmit={handleSearchSubmit} className={`relative flex flex-col w-full rounded-2xl overflow-hidden ${theme === 'dark' ? 'bg-dark-bg-elevated' : 'bg-white'}`}>
                    <textarea
                        ref={textareaRef}
                        name="input"
                        placeholder="Ask anything about Indian Commercial Court Law..."
                        spellCheck="false"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        className={`
                            w-full p-5 pr-14 outline-none resize-none text-base md:text-lg min-h-[60px] max-h-[200px] bg-transparent
                            ${theme === 'dark' ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}
                        `}
                        rows={1}
                    />

                    <div className="flex justify-between items-center px-4 pb-3">
                        <div className="flex items-center space-x-2">
                            <button
                                type="button"
                                className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                                title="Attach file"
                            >
                                <Paperclip size={18} />
                            </button>
                            <button
                                type="button"
                                className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                                title="Voice input"
                            >
                                <Mic size={18} />
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={!inputValue.trim() || isLoading}
                            className={`
                                p-2.5 rounded-xl flex items-center justify-center transition-all duration-200
                                ${inputValue.trim()
                                    ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-md transform hover:scale-105'
                                    : theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
                            `}
                        >
                            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                        </button>
                    </div>
                </form>
            </div>

            {/* Suggestions */}
            {!inputValue && (
                <div className="w-full max-w-3xl">
                    <p className={`text-center text-xs uppercase tracking-widest font-semibold mb-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        Suggested Queries
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {defaultPrompts.map((item, index) => (
                            <button
                                key={index}
                                onClick={() => setInputValue(item.prompt)}
                                className={`
                                    flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border group
                                    ${theme === 'dark'
                                        ? 'bg-dark-bg-elevated border-dark-border text-gray-300 hover:bg-dark-bg-tertiary hover:border-gray-600 hover:shadow-lg hover:shadow-primary-900/10'
                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-primary-200 hover:text-primary-700 hover:shadow-md'}
                                `}
                            >
                                <span className="group-hover:scale-110 transition-transform duration-200">{item.icon}</span>
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Searchbar;
